/**
 * AppContainer — Sandboxed iframe renderer for agent-built "apps".
 *
 * Security model:
 * - sandbox="allow-scripts" (NO allow-same-origin)
 * - Cannot access parent DOM, cookies, localStorage
 * - Design system CSS variables + utility classes injected via <style> tag
 * - postMessage bridge for app→workspace communication
 */

import React, { useRef, useEffect, useCallback, useState } from "react";
import sandboxStyles from "@agentme/design-system/src/sandbox/sandbox-styles.css?raw";
import { emitUxTelemetry } from "../telemetry/uxTelemetry";
import { validateCompiledWorkspaceHtml } from "../../api/workspace-apps/validate-compiled-html.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AppData {
  id: string;
  title: string;
  html: string;
  designSystem: boolean;
  /**
   * "render_spec" — HTML was compiled from a RenderSpec; strict validation
   *   runs before mounting. Violations render a safe fallback.
   * "legacy_html" — Arbitrary agent HTML; mounted unchanged (report-only mode).
   *
   * Defaults to "legacy_html" when omitted for backwards compatibility.
   */
  sourceMode?: "render_spec" | "legacy_html";
  /** Template ID used when this app was generated via a RenderSpec template (optional). */
  templateId?: string;
  /** Compiler version that produced the RenderSpec fragment (optional). */
  compilerVersion?: string;
}

interface AppContainerProps {
  app: AppData;
  onAction?: (action: AppAction) => void;
  onClose?: () => void;
  fullScreen?: boolean;
  theme?: "light" | "dark";
}

export interface AppAction {
  type: "navigate" | "approve" | "request-data" | "close" | "custom";
  payload?: Record<string, unknown>;
}

// ─── Design system tokens (matching packages/design-system/src/css/tokens.css) ─

const LIGHT_TOKENS = `
  --c-bg: #fafafa;
  --c-surface: #ffffff;
  --c-surface-2: #f5f5f5;
  --c-surface-3: #ebebeb;
  --c-border: rgba(0, 0, 0, 0.04);
  --c-border-hover: rgba(0, 0, 0, 0.08);
  --c-text: #1a1a1a;
  --c-text-secondary: #6b6b6b;
  --c-text-faint: #9b9b9b;
  --c-primary: #1a61ed;
  --c-primary-hover: #1554d4;
  --c-primary-subtle: rgba(26, 97, 237, 0.1);
  --c-primary-text: #ffffff;
  --c-accent: #f2c94c;
  --c-accent-hover: #e5bc3f;
  --c-accent-subtle: rgba(242, 201, 76, 0.12);
  --c-accent-text: #1a1a1a;
  --c-danger: #e5484d;
  --c-success: #12a594;
  --c-warning: #d97706;
  --c-info: #1a61ed;
  --c-hover: rgba(0, 0, 0, 0.02);
  --c-active: rgba(0, 0, 0, 0.04);
  --c-badge-muted-bg: rgba(0, 0, 0, 0.04);
  --c-badge-muted-text: #6b6b6b;
  --c-badge-muted-border: rgba(0, 0, 0, 0.08);
  --c-input-bg: #ffffff;
  --c-shadow: 0 1px 3px rgba(0, 0, 0, 0.03), 0 1px 2px rgba(0, 0, 0, 0.02);
`;

const DARK_TOKENS = `
  --c-bg: #0a0a0b;
  --c-surface: #111113;
  --c-surface-2: #19191c;
  --c-surface-3: #222226;
  --c-border: rgba(255, 255, 255, 0.14);
  --c-border-hover: rgba(255, 255, 255, 0.22);
  --c-text: #e8e8ed;
  --c-text-secondary: #8b8b97;
  --c-text-faint: #78788a;
  --c-primary: #5b8def;
  --c-primary-hover: #7aa3f5;
  --c-primary-subtle: rgba(91, 141, 239, 0.12);
  --c-primary-text: #ffffff;
  --c-accent: #f2c94c;
  --c-accent-hover: #f5d36a;
  --c-accent-subtle: rgba(242, 201, 76, 0.15);
  --c-accent-text: #1a1a1a;
  --c-danger: #e5484d;
  --c-success: #0eb39e;
  --c-warning: #f59e0b;
  --c-info: #5b8def;
  --c-hover: rgba(255, 255, 255, 0.02);
  --c-active: rgba(255, 255, 255, 0.04);
  --c-badge-muted-bg: rgba(255, 255, 255, 0.04);
  --c-badge-muted-text: #8b8b97;
  --c-badge-muted-border: rgba(255, 255, 255, 0.06);
  --c-input-bg: #111113;
  --c-shadow: none;
`;

// Sandbox stylesheet: utility classes for agent-built apps (.card, .badge, .btn, etc.)
// Loaded via Vite ?raw import so it inlines as a string at build time.
const SANDBOX_CSS = sandboxStyles ?? "";

function buildDesignSystemCss(theme: "light" | "dark"): string {
  const tokens = theme === "dark" ? DARK_TOKENS : LIGHT_TOKENS;
  return `:root {
${tokens}
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
  color: var(--c-text);
  background-color: var(--c-bg);
}
* { box-sizing: border-box; margin: 0; padding: 0; }
body { margin: 0; padding: 12px; font-size: 13px; line-height: 1.5; }
${SANDBOX_CSS}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppContainer({
  app,
  onAction,
  onClose,
  fullScreen = false,
  theme = "light",
}: AppContainerProps): React.ReactElement {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    emitUxTelemetry("ux.agent_element.rendered", {
      elementType: "app",
      elementId: app.id,
    });
  }, [app.id]);

  // Determine the HTML body to use — may be replaced by safe fallback for render_spec violations
  const resolvedHtml = useCallback((): string => {
    const sourceMode = app.sourceMode ?? "legacy_html";
    if (sourceMode !== "render_spec") return app.html;

    const result = validateCompiledWorkspaceHtml(app.html, "strict", SANDBOX_CSS);
    if (result.kind === "ok") return app.html;

    // Blocked — return safe fallback markup using the .empty-state design-system class
    const reason =
      result.kind === "blocked"
        ? result.restriction.evidence
        : "Unknown validation error";
    return `<div class="empty-state">
  <div class="empty-state-icon">&#x26A0;</div>
  <div class="empty-state-title">App validation failed</div>
  <div class="empty-state-desc">${reason.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
</div>`;
  }, [app.html, app.sourceMode]);

  // Build the full HTML document for the iframe
  const buildDocument = useCallback(() => {
    const contentHtml = resolvedHtml();
    const designCss = app.designSystem ? `<style>${buildDesignSystemCss(theme)}</style>` : "";
    // Escape app.id for safe interpolation into a JS string literal
    const safeAppId = app.id.replace(/\\/g, "\\\\").replace(/'/g, "\\'").replace(/</g, "\\x3c");
    return `<!DOCTYPE html>
<html class="${theme}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data: blob:;">
  ${designCss}
  <script>
    // postMessage bridge: send actions to parent workspace
    window.agentAction = function(type, payload) {
      window.parent.postMessage(
        { source: 'workspace-app', appId: '${safeAppId}', action: { type, payload } },
        '*'
      );
    };

    (function() {
      const SELECTED_CLASS = 'agentme-ui-selected';

      const styleEl = document.createElement('style');
      styleEl.textContent = '.' + SELECTED_CLASS + ' { outline: 1px solid var(--c-accent); outline-offset: 2px; border-radius: 6px; }';
      document.head.appendChild(styleEl);

      let selectedEl = null;

      const isEditable = (el) =>
        el instanceof HTMLElement &&
        (el.isContentEditable || Boolean(el.closest('input, textarea, select, option, [contenteditable="true"]')));

      const isAccordionLike = (el) => {
        if (!(el instanceof HTMLElement)) return false;
        const expandable = el.closest(
          [
            '[aria-expanded]',
            '[aria-controls]',
            '[data-accordion]',
            '[data-disclosure]',
            '[data-expandable]',
            '[data-collapsible]',
            'summary',
            'details',
            '[data-state="open"]',
            '[data-state="closed"]',
            '[data-state="collapsed"]',
            '[data-state="expanded"]',
          ].join(', ')
        );
        if (!expandable) return false;
        return true;
      };

      const selectorFor = (el) => {
        if (!(el instanceof HTMLElement)) return 'unknown';
        if (el.id) return '#' + el.id;
        const role = el.getAttribute('role');
        if (role) return el.tagName.toLowerCase() + '[role="' + role + '"]';
        return el.tagName.toLowerCase();
      };

      const readTargetIds = (el) => {
        if (!(el instanceof HTMLElement)) return {};
        return {
          objectId: el.dataset.objectId || undefined,
          objectKind: el.dataset.objectKind || undefined,
          fileId: el.dataset.fileId || undefined,
          actionId: el.dataset.actionId || undefined,
          messageId: el.dataset.messageId || undefined,
          conversationId: el.dataset.conversationId || undefined,
          resourceUri: el.dataset.resourceUri || undefined,
        };
      };

      const labelFor = (el) => {
        if (!(el instanceof HTMLElement)) return 'element';
        const aria = (el.getAttribute('aria-label') || '').trim();
        if (aria) return aria.slice(0, 80);
        const title = (el.getAttribute('title') || '').trim();
        if (title) return title.slice(0, 80);
        const text = (el.textContent || '').replace(/\\s+/g, ' ').trim();
        if (text) return text.slice(0, 80);
        return el.tagName.toLowerCase();
      };

      const findSelectable = (start) => {
        let current = start instanceof HTMLElement ? start : null;
        while (current && current !== document.body) {
          const rect = current.getBoundingClientRect();
          const hasArea = rect.width >= 24 && rect.height >= 16;
          const notTooLarge =
            rect.width < window.innerWidth * 0.96 && rect.height < window.innerHeight * 0.9;
          const label = labelFor(current);
          if (hasArea && notTooLarge && label.length > 0) {
            return current;
          }
          current = current.parentElement;
        }
        return null;
      };

      const markSelected = (el) => {
        if (!(el instanceof HTMLElement)) return;
        if (selectedEl && selectedEl !== el) selectedEl.classList.remove(SELECTED_CLASS);
        selectedEl = el;
        selectedEl.classList.add(SELECTED_CLASS);
      };

      document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (isEditable(target)) return;
        const selectable = findSelectable(target);
        if (!selectable) return;
        markSelected(selectable);
      }, true);

      document.addEventListener('dblclick', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (isEditable(target) || isAccordionLike(target)) return;

        const selectable = findSelectable(target);
        if (!selectable) return;
        markSelected(selectable);

        const rect = selectable.getBoundingClientRect();
        window.agentAction('custom', {
          kind: 'contextual_chat_open',
          source: 'app',
          label: labelFor(selectable),
          selector: selectorFor(selectable),
          ...readTargetIds(selectable),
          pointX: event.clientX,
          pointY: event.clientY,
          targetRect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        });
      }, true);

      // ── Delegated data-agent-action-* listeners ──────────────────────────
      // Handles clicks/submits on elements with data-agent-action-type and
      // data-agent-action-payload attributes, forwarding via window.agentAction.
      // This is the preferred pattern for render_spec compiled fragments.
      document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const el = target.closest('[data-agent-action-type]');
        if (!el || !(el instanceof HTMLElement)) return;
        const actionType = el.dataset.agentActionType;
        if (!actionType) return;
        let payload = {};
        const rawPayload = el.dataset.agentActionPayload;
        if (rawPayload) {
          try { payload = JSON.parse(rawPayload); } catch { payload = { raw: rawPayload }; }
        }
        window.agentAction(actionType, payload);
      });

      document.addEventListener('submit', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const el = target.closest('[data-agent-action-type]');
        if (!el || !(el instanceof HTMLElement)) return;
        const actionType = el.dataset.agentActionType;
        if (!actionType) return;
        event.preventDefault();
        let payload = {};
        const rawPayload = el.dataset.agentActionPayload;
        if (rawPayload) {
          try { payload = JSON.parse(rawPayload); } catch { payload = { raw: rawPayload }; }
        }
        window.agentAction(actionType, payload);
      });
    })();
  </script>
</head>
<body>
${contentHtml}
</body>
</html>`;
  }, [app.id, app.html, app.sourceMode, app.designSystem, theme, resolvedHtml]);

  // Listen for postMessage from the iframe
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Security: only accept messages from our own iframe
      if (event.source !== iframeRef.current?.contentWindow) return;
      // Sandboxed iframes without allow-same-origin have origin "null"
      if (event.origin !== "null") return;

      const data = event.data;
      if (data?.source === "workspace-app" && data?.appId === app.id && data?.action) {
        const incomingAction = data.action as AppAction;
        if (incomingAction.type === "custom" && incomingAction.payload) {
          const payload = { ...(incomingAction.payload as Record<string, unknown>) };
          const iframeRect = iframeRef.current?.getBoundingClientRect();
          const pointX = typeof payload.pointX === "number" ? Number(payload.pointX) : null;
          const pointY = typeof payload.pointY === "number" ? Number(payload.pointY) : null;
          const targetRect =
            payload.targetRect && typeof payload.targetRect === "object"
              ? (payload.targetRect as Record<string, unknown>)
              : null;

          if (iframeRect && pointX != null && pointY != null) {
            payload.viewportX = Math.round(iframeRect.left + pointX);
            payload.viewportY = Math.round(iframeRect.top + pointY);
          }
          if (targetRect) {
            if (typeof targetRect.width === "number") {
              payload.targetWidth = Number(targetRect.width);
            }
            if (typeof targetRect.height === "number") {
              payload.targetHeight = Number(targetRect.height);
            }
          }

          onAction?.({ ...incomingAction, payload });
          return;
        }

        onAction?.(incomingAction);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [app.id, onAction]);

  // Set iframe content via srcdoc
  useEffect(() => {
    setLoading(true);
    // Small delay to allow iframe to initialize
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [app.html]);

  const containerClass = fullScreen ? "fixed inset-0 z-50 flex flex-col" : "flex flex-col h-full";

  return (
    <div className={containerClass} style={{ backgroundColor: "var(--c-bg)" }}>
      {/* App title bar */}
      <div
        className="flex items-center justify-between px-3 py-2 shrink-0"
        style={{
          backgroundColor: "var(--c-surface)",
          borderBottom: "1px solid var(--c-border)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: "var(--c-primary-subtle)",
              color: "var(--c-primary)",
            }}
          >
            APP
          </span>
          <span className="text-xs font-medium truncate" style={{ color: "var(--c-text)" }}>
            {app.title}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded transition-colors"
              style={{ color: "var(--c-text-faint)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
              title="Close app"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Sandboxed iframe */}
      <div className="flex-1 relative min-h-0">
        {loading && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ backgroundColor: "var(--c-bg)" }}
          >
            <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
              Loading app...
            </span>
          </div>
        )}
        <iframe
          ref={iframeRef}
          srcDoc={buildDocument()}
          sandbox="allow-scripts"
          title={`App: ${app.title}`}
          className="w-full h-full border-0"
          style={{
            opacity: loading ? 0 : 1,
            transition: "opacity 150ms ease",
          }}
          onLoad={() => setLoading(false)}
        />
      </div>
    </div>
  );
}
